/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import * as emotion from "emotion";
import dayjs from "../../utils/dayjs";
import {BaseComponent} from "@intuitionrobotics/thunderstorm/frontend";
import {COLORS} from "@res/colors";
import {FONTS} from "@res/fonts";
import {generateHex} from "@intuitionrobotics/ts-common/utils/random-tools";
import {PsychologicalQuestion} from "@app/app-shared/patient-data";

const wrapper = emotion.css(
	{
		width: "100%",
		backgroundColor: COLORS.white,
		boxSizing: "border-box"
	}
);

const rowHeight = 60;
const headerHeight = 30;

const body = emotion.css(
	{
		borderTop: `1px solid ${COLORS.color_gray_medium}`,
		borderBottom: `1px solid ${COLORS.color_gray_medium}`,
		boxSizing: "border-box"
	}
);

const lastScoreFrame = emotion.css(
	{
		height: 208,
		minWidth: 100,
		maxWidth: 208,
		width: "15vw",
		backgroundColor: "rgba(166, 170, 178, 0.1)",
		boxSizing: "border-box"
	}
);

const tableWrapper = emotion.css(
	{
		flex: 1,
		boxSizing: "border-box"
	}
);

const questionArea = emotion.css(
	{
		height: "100%",
		// paddingLeft: 30,
		flex: 1,
		boxSizing: "border-box"
	}
);

const scoresTable = emotion.css(
	{
		height: "100%",
		boxSizing: "border-box",
		paddingRight: 30
		// flexDirection: "row-reverse"
	}
);

const questionAreaHeader = emotion.css(
	{
		height: headerHeight,
		borderBottom: `1px solid ${COLORS.color_gray_medium}`
	}
);

const scoreLabel = emotion.css(
	{
		height: 55
	}
);

const questionDescription = emotion.css(
	{
		height: rowHeight,
		borderBottom: `1px solid ${COLORS.color_gray_medium}`
	}
);

const score = emotion.css(
	{
		borderLeft: `1px solid ${COLORS.neutral_gray_1}`,
		height: "100%",
		maxWidth: 100,
		minWidth: 60,
		width: "4vw",
		boxSizing: "border-box"
	}
);

const scoreHeader = emotion.css(
	{
		justifyContent: "center",
		height: headerHeight,
		borderBottom: `1px solid ${COLORS.color_gray_medium}`
	}
);

const scoreResult = emotion.css(
	{
		height: rowHeight,
		borderBottom: `1px solid ${COLORS.color_gray_medium}`,
		justifyContent: "center"
	}
);

const scoreSum = emotion.css(
	{
		height: 55,
		justifyContent: "center"
	}
);

type State = {
	latestSum?: number;
	latestDate?: number
};

type Props = {
	questions: PsychologicalQuestion[];
	data: { date: number, scores: number[] }[];
};


export class PsychologicalDataTable
	extends BaseComponent<Props, State> {

	constructor(props: Props) {
		super(props);
		this.state = {
			latestDate: undefined,
			latestSum: undefined
		};
	}

	componentDidMount() {
		this.changeLatestData();
		this.forceUpdate();
	}

	componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any) {
		if (prevProps.data !== this.props.data) {
			this.changeLatestData();
			this.forceUpdate();
		}
	}

	changeLatestData = () => {
		if (this.props.data.length) {
			const latest = this.props.data[this.props.data.length - 1];
			const latestDate = latest.date;
			const latestSum = this.calculateSum(latest.scores);
			this.setState({latestSum, latestDate});
		} else {
			this.setState({latestSum: undefined, latestDate: undefined});
		}
	};

	calculateSum = (scores: number[]) => {
		if (!scores.length)
			return undefined;

		let sum = 0;
		for (const _score of scores) {
			if (_score !== -1)
				sum += _score;
		}

		return sum;
	};

	render() {
		return <div className={`${wrapper} ll_v_s`}>
			<div className={`${body} ll_h_c`}>
				{this.renderTable()}
				{this.renderLastScoreFrame()}
			</div>
		</div>;
	}

	renderLastScoreFrame = () => {
		return <div className={`${lastScoreFrame}`}>
			<div className={`ll_h_c match_height`}>
				<div className={`ll_v_c`} style={{margin: "auto"}}>
					<div>
						<span>
							{FONTS.bold_60(`${this.state.latestSum !== undefined ? this.state.latestSum : "-"}`,
							               {color: this.state.latestSum && this.isOutOfRange(this.state.latestSum) ? COLORS.secondary_red : COLORS.black})}
						</span>
						<span>
							{FONTS.reg_60(`/${this.props.questions.length * 3}`, {color: "#979797", opacity: 0.6, fontFamily: "Inter-Thin"})}
						</span>
					</div>
					<div>{FONTS.bold_15("Last Score")}</div>
					<div>{FONTS.reg_11(
						`${this.state.latestDate !== undefined ?
							dayjs(this.state.latestDate).format("MMM Do") : "-"}`,
						{color: COLORS.black})}</div>
				</div>
			</div>
		</div>;
	};

	private isOutOfRange = (latestSum: number) => latestSum >= 3;

	renderTable = () => {
		return <div className={`${tableWrapper} ll_h_s`}>
			{this.renderQuestionArea()}
			{this.renderScoresTable()}
		</div>;
	};

	renderQuestionArea = () => {
		return <div className={`${questionArea} ll_v_s`}>
			<div className={`${questionAreaHeader} ll_h_c`}>
				{FONTS.medium_12("Question", {color: COLORS.black})}
			</div>
			<div className={`ll_v_s`}>
				{
					this.props.questions.map(question => {
						return <div key={question.key}
						            className={`${questionDescription} ll_h_c`}>
							{FONTS.reg_14(question.description, {color: COLORS.slateGrey})}
						</div>;
					})
				}
			</div>
			<div className={`${scoreLabel} ll_h_c`}>
				{FONTS.reg_14("Score", {fontFamily: "Inter-Black", color: COLORS.slateGrey})}
			</div>
		</div>;
	};

	renderScoresTable = () => {
		const data = this.props.data;
		return <div className={`${scoresTable} ll_h_s`}>
			{
				data.map((content: { date: number, scores: number[] }/*, i*/) => {
					// const rightMost = i === 0;
					return this.renderScore(content/*, rightMost, i === (data.length - 1)*/);
				})
			}
		</div>;
	};

	renderScore = (content: { date: number, scores: number[] }/*, borderRight: boolean, isLatest = false*/) => {
		if (content.scores.length < 1)
			return null;

		const sum = this.calculateSum(content.scores);

		return <div className={`${score} ll_v_s`}
		            key={generateHex(8)}>
		            {/*style={{borderRight: borderRight ? `1px solid ${COLORS.neutral_gray_1}` : undefined}}>*/}
			<div className={`${scoreHeader} ll_h_c`}>
			     {/*style={{backgroundColor: isLatest ? "rgba(166, 170, 178, 0.1)" : "white"}}>*/}
				{FONTS.medium_11(dayjs(content.date).format("MM/DD"), {color: COLORS.black})}
			</div>
			<div className={`ll_v_s`}>
				{
					content.scores.map(number => {
						if (number === -1) {
							return <div key={generateHex(8)}
							            className={`${scoreResult} ll_h_c`}>-</div>;
						} else {
							return <div key={generateHex(8)}
							            className={`${scoreResult} ll_h_c`}>
								{FONTS.black_18(number.toString(), {color: COLORS.black})}
							</div>;
						}
					})
				}
			</div>
			<div className={`${scoreSum} ll_h_c`}>
				<div>
					{
						sum === undefined ?
							FONTS.black_23(
								`-`,
								{color: COLORS.black}
							) :
							FONTS.black_23(
								`${sum}`,
								{color: this.isOutOfRange(sum) ? COLORS.secondary_red : COLORS.black}
							)
					}
					{
						FONTS.light_23(
							`/${3 * this.props.questions.length}`,
							{color: "#c0c0c0"}
						)
					}
				</div>
			</div>
		</div>;
	};
}
